import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Copy, Ticket, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAxios } from '../lib/adminApi';

const emptyCoupon = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_order_value: 0,
  max_uses: 1,
  is_active: true,
  notes: '',
};

export const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyCoupon);
  const [singleUse, setSingleUse] = useState(true);

  const fetchCoupons = async () => {
    try {
      const { data } = await adminAxios.get('/coupons');
      setCoupons(data);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const generateCode = () => {
    const code = 'KS' + Math.random().toString(36).substring(2, 8).toUpperCase();
    setForm({ ...form, code });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code) { toast.error('Please enter a code'); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        min_order_value: parseFloat(form.min_order_value) || 0,
        max_uses: singleUse ? 1 : (form.max_uses ? parseInt(form.max_uses) : null),
      };
      await adminAxios.post('/coupons', payload);
      toast.success(`Coupon ${form.code.toUpperCase()} created!`);
      setForm(emptyCoupon);
      await fetchCoupons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await adminAxios.delete(`/coupons/${id}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch {
      toast.error('Failed to delete coupon');
    }
  };

  const handleToggle = async (coupon) => {
    try {
      const payload = {
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_order_value: coupon.min_order_value,
        max_uses: coupon.max_uses,
        is_active: !coupon.is_active,
        notes: coupon.notes || '',
      };
      await adminAxios.put(`/coupons/${coupon.id}`, payload);
      toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch {
      toast.error('Failed to update coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <div data-testid="admin-coupons-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <Link to="/admin" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors">
              <ArrowLeft size={16} className="mr-2" strokeWidth={1.5} /> Back to Admin
            </Link>
            <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground flex items-center gap-4">
              <Ticket className="text-primary" size={48} strokeWidth={1.5} />
              <span>Coupons <span className="italic">& Vouchers</span></span>
            </h1>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-primary/20 p-8 mb-12 bg-background/50"
        >
          <h2 className="font-display text-2xl mb-6">Create New Coupon</h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">Coupon Code *</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  required
                  data-testid="coupon-code-input"
                  placeholder="e.g. WELCOME10"
                  className="flex-1 bg-transparent border-b border-white/20 rounded-none px-0 py-3 focus:border-primary focus:outline-none text-lg uppercase tracking-wider"
                />
                <button type="button" onClick={generateCode} className="text-xs uppercase tracking-widest px-4 border border-primary/30 text-primary hover:bg-primary/10 transition-colors">
                  Auto Generate
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">Discount Type *</label>
              <select
                value={form.discount_type}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                data-testid="coupon-type-select"
                className="w-full bg-background border-b border-white/20 py-3 focus:border-primary focus:outline-none text-lg"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">
                Discount Value * {form.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                required
                data-testid="coupon-value-input"
                className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-lg"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">Minimum Order Value (₹)</label>
              <input
                type="number"
                min="0"
                value={form.min_order_value}
                onChange={(e) => setForm({ ...form, min_order_value: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-lg"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">
                Usage Type
              </label>
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={singleUse} onChange={() => setSingleUse(true)} className="w-4 h-4" />
                  <span className="text-sm">Single-use</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!singleUse} onChange={() => setSingleUse(false)} className="w-4 h-4" />
                  <span className="text-sm">Multi-use</span>
                </label>
              </div>
            </div>

            {!singleUse && (
              <div>
                <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">Max Uses (leave empty = unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={form.max_uses || ''}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-lg"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">Notes (internal)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. For VIP customer Mr. Sharma"
                className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-lg"
              />
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={creating}
                data-testid="create-coupon-button"
                className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Plus size={18} strokeWidth={1.5} />
                <span>{creating ? 'Creating...' : 'Create Coupon'}</span>
              </button>
            </div>
          </form>
          <p className="text-xs text-muted-foreground mt-4">
            <strong className="text-primary">Single-use:</strong> each customer email can only redeem this coupon once, and total redemptions are capped at 1 globally.
            <strong className="text-primary ml-2">Multi-use:</strong> many customers can use it; each email can still only redeem once.
          </p>
        </motion.div>

        <h2 className="font-display text-3xl mb-6">All Coupons ({coupons.length})</h2>
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : coupons.length === 0 ? (
          <p className="text-muted-foreground italic">No coupons yet. Create one above.</p>
        ) : (
          <div className="space-y-3" data-testid="coupons-list">
            {coupons.map((c) => (
              <div key={c.id} className={`border p-6 flex flex-col md:flex-row md:items-center gap-4 ${c.is_active ? 'border-primary/20' : 'border-white/5 opacity-60'}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-xl text-primary tracking-wider">{c.code}</span>
                    <button onClick={() => copyCode(c.code)} className="text-muted-foreground hover:text-primary">
                      <Copy size={14} strokeWidth={1.5} />
                    </button>
                    <span className={`text-xs uppercase tracking-widest px-2 py-1 border rounded-full ${c.is_active ? 'border-green-500/40 text-green-400' : 'border-white/20 text-muted-foreground'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>
                      {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                    </span>
                    {c.min_order_value > 0 && <span>• Min ₹{c.min_order_value}</span>}
                    <span>
                      • Used: {c.used_count}{c.max_uses ? `/${c.max_uses}` : ' (unlimited)'}
                    </span>
                  </div>
                  {c.notes && <p className="text-xs text-muted-foreground mt-1 italic">{c.notes}</p>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggle(c)}
                    className="text-xs uppercase tracking-widest px-4 py-2 border border-white/20 hover:border-primary hover:text-primary transition-colors"
                    data-testid={`toggle-coupon-${c.id}`}
                  >
                    {c.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(c.id, c.code)}
                    className="text-destructive hover:text-destructive/80 p-2"
                    data-testid={`delete-coupon-${c.id}`}
                  >
                    <Trash2 size={18} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
